import { useState, type ReactNode } from 'react';
import { Box, Button, Drawer } from '@glplus/glpweb-dsl';
import { useTranslation } from 'react-i18next';
import MainHeader, { type NavLink } from './MainHeader';
import MainFooter from './MainFooter';
import { SideNavigation } from './SideNavigation';
import { useInboxCounts } from '../lib/inbox';
import type { RunnerNavigationCounts } from '../lib/useRunnerNavigationCounts';
import { runnerTheme } from '../styles/runnerTheme';

export function AppShell({ nav, displayName, email, showInbox = false, topNotice, navigationCounts, children }: { nav: NavLink[]; displayName?: string | null; email?: string | null; showInbox?: boolean; topNotice?: ReactNode; navigationCounts?: RunnerNavigationCounts; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const inboxCounts = useInboxCounts(showInbox);
  const { unreadTotal } = inboxCounts;
  const navigation = <SideNavigation nav={nav} showInbox={showInbox} unreadTotal={unreadTotal} counts={navigationCounts} onNavigate={() => setMenuOpen(false)} />;
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f6f8' }}>
      {/* Keyboard users otherwise tab through the brand link and seven nav
          links before reaching content on every page (WCAG 2.4.1). Hidden
          until focused — see .glp-skip-link in styles/global.css. */}
      <a href="#main" className="glp-skip-link">{t('app.skipToContent')}</a>
      <MainHeader nav={nav} displayName={displayName} email={email} showInbox={showInbox} onMenuOpen={() => setMenuOpen(true)} inboxCounts={inboxCounts} />
      <Box sx={{ display: 'flex', flex: 1, minWidth: 0 }}>
      <Box sx={{ display: { xs: 'none', md: 'block' }, width: 248, flexShrink: 0, bgcolor: runnerTheme.white, position: 'sticky', top: 64, height: 'calc(100dvh - 64px)', overflowY: 'auto', borderRight: `1px solid ${runnerTheme.line}` }}>{navigation}</Box>
      <Drawer open={menuOpen} onClose={() => setMenuOpen(false)} width="min(320px, 90vw)">
        <Box sx={{ bgcolor: runnerTheme.navy, px: 2, minHeight: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box component="img" src={`/brand/runner-color-${i18n.language.startsWith('es') ? 'es' : 'en'}.png`} alt={t('runnerNav.logo')} sx={{ width: 190, height: 44, objectFit: 'contain' }} />
          <Button variant="iconButton" startIcon="close" ariaLabel={t('runnerNav.close')} onClick={() => setMenuOpen(false)} sx={{ color: '#fff', '& svg': { color: '#fff' } }} />
        </Box>
        {menuOpen && navigation}
      </Drawer>
      <Box component="main" id="main" data-testid="app-main" tabIndex={-1} sx={{ outline: 'none', flex: 1, minWidth: 0, width: '100%' }}>
        {topNotice}
        <Box sx={{ px: { xs: '16px', sm: '32px' }, py: { xs: 3, md: '28px' }, minWidth: 0, maxWidth: 1280, mx: 'auto' }}>{children}</Box>
      </Box>
      </Box>
      <MainFooter />
    </Box>
  );
}
